import jsPDF from "jspdf";
import "jspdf-autotable";
import {
  getA3ArticuloDescripcion,
  getA3PedidoLineasMatriz,
  getA3ProveedorFichaPdf,
} from "../api/Produccion/a3ArticulosApi";
import { getEmpresaPdfFromPedidoRow } from "../constants/a3EmpresaPdf";
import {
  colorLabel,
  fmtQtyMatriz,
  matrixViewFromSavedLineas,
  normStr,
  tallaLabel,
} from "../components/Produccion/a3PedidoMatrizUtils";
import {
  createPrintPalette,
  drawInfoCard,
  drawPageFooter,
  drawPartyBlock,
  drawPdfHeader,
  drawSectionTitle,
  loadPdfImage,
  normalizeBrandRgb,
} from "./stagePdfPrintLayout";

/** Etiquetas visibles del PDF (inglés). */
const PDF_LABELS = {
  purchaseOrder: "Purchase Order",
  supplier: "Supplier",
  taxId: "Tax ID",
  phone: "Phone",
  fax: "Fax",
  email: "Email",
  paymentTerms: "Payment terms",
  date: "Date",
  item: "Item",
  reference: "Reference",
  budget: "Budget",
  units: "Units",
  unitPrice: "Unit price",
  documentTotal: "Document total",
  sizeColorBreakdown: "Size / color breakdown",
  columnTotal: "Column total",
  page: "Page",
  matrixMixedFamilies:
    "Could not organize the matrix grid (mixed size families).",
  noSizeColorBreakdown: "No size/color breakdown in A3 for this order.",
  totalOrderedQty: (qty) => `Total ordered quantity: ${qty} pcs`,
};

const fmtNum = (n) =>
  Number(n ?? 0).toLocaleString("es-ES", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const fmtDate = (raw) => {
  if (!raw) return "—";
  try {
    const dt = new Date(raw);
    if (Number.isNaN(dt.getTime())) return String(raw);
    return dt.toLocaleDateString("es-ES");
  } catch {
    return String(raw);
  }
};

const getPoCompraRef = (row) => {
  if (row?.SerieCompra != null && row?.NumDocCompra != null) {
    return `${String(row.TipoContableCompra || "1").trim()}/${String(row.SerieCompra).trim()}-${row.NumDocCompra}`;
  }
  return "";
};

const resolveUnitPrice = (unidades, precioUn, total, totalDocumento) => {
  const u = Number(unidades);
  const p = Number(precioUn);
  const t = Number(total);
  const td = Number(totalDocumento);
  const hasU = Number.isFinite(u) && u > 0;
  const hasP = Number.isFinite(p);
  const hasTD = Number.isFinite(td);
  const baseTotal = hasTD ? td : t;
  const hasBaseTotal = Number.isFinite(baseTotal);
  if (!hasU) return hasP ? p : null;
  if (!hasP && hasBaseTotal) return Number((baseTotal / u).toFixed(6));
  if (hasP && hasBaseTotal) {
    const implied = Number((baseTotal / u).toFixed(6));
    if (Number.isFinite(implied) && Math.abs(implied - p) > 0.01)
      return implied;
  }
  return hasP ? p : hasBaseTotal ? Number((baseTotal / u).toFixed(6)) : null;
};

/** Stage: descripciones A3 largas → iniciales de impresión. */
const stageSizeInitial = (t) => {
  const desc = normStr(t?.DESCRIPCION);
  const cod = normStr(t?.CODTALLA);
  if (!desc) return cod.length <= 4 ? cod.toUpperCase() : cod;
  const n = desc
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
  if (/chiquit/.test(n)) return "CH";
  if (/extra\s*grand|extragrand/.test(n)) return "EG";
  if (/median/.test(n)) return "M";
  if (/grand/.test(n)) return "G";
  if (desc.length <= 4) return desc.toUpperCase();
  return cod.length <= 4 ? cod.toUpperCase() : cod || desc;
};

const sizeColLabel = (t, empresaKey) => {
  if (empresaKey === "STAGE_DESIGN") return stageSizeInitial(t);
  const d = normStr(t?.DESCRIPCION);
  if (d && d.length <= 4) return d.toUpperCase();
  return d || tallaLabel(t);
};

function mapApiLineasToNorm(rows) {
  return (Array.isArray(rows) ? rows : []).map((x) => ({
    codFamTallaH: String(x.codFamTallaH || "").trim(),
    codTallaH: String(x.codTallaH || "").trim(),
    codTallaHDesc: String(x.descTallaH || "").trim(),
    codFamTallaV: String(x.codFamTallaV || "").trim(),
    codTallaV: String(x.codTallaV || "").trim(),
    codTallaVDesc: String(x.descTallaV || "").trim(),
    unidades: Number(x.unidades) || 0,
  }));
}

const formatItemWithDescription = (codart, descripcion) => {
  const cod = normStr(codart);
  const desc = normStr(descripcion);
  if (!cod) return "—";
  if (!desc) return cod;
  return `${cod} — ${desc}`;
};

/** A3 FORPAG "C" = Contado → etiqueta en inglés para el PDF. */
const formatPaymentTermsForPdf = (ficha) => {
  const code = normStr(ficha?.forpagCode).toUpperCase();
  const desc = normStr(ficha?.forpagDescripcion);
  if (code === "C" || desc.toUpperCase() === "C") return "Cash";
  if (/^contado$/i.test(desc)) return "Cash";
  return desc || code;
};

/**
 * Genera y descarga el PDF del Purchase Order A3 (proveedor + matriz tallas/colores).
 * @param {object} row — fila de PresupuestoA3Pedido / vínculo CRM–A3
 */
export async function printPedidoCompraPdf(row) {
  const idPedc = Number(row?.IdPedc);
  if (!Number.isFinite(idPedc) || idPedc <= 0) {
    throw new Error("No hay Purchase Order (IdPedc) para imprimir");
  }

  const codart = String(row?.CodArt || "").trim();
  const emp = getEmpresaPdfFromPedidoRow(row);
  const empresaKey = emp.key || "STAGE_DESIGN";
  const brandRgb = normalizeBrandRgb(emp?.headerRgb);
  const palette = createPrintPalette(brandRgb);
  const dbSource = String(row?.EmpresaA3 || empresaKey).trim();
  const poRef = getPoCompraRef(row) || `PO-${idPedc}`;
  const codpro = normStr(row?.CodPro);

  const [matrizResult, fichaResult, artDescResult] = await Promise.all([
    getA3PedidoLineasMatriz({
      idPedc,
      idPedv: row?.IdPedv,
      codart,
      dbSource,
      fallbackByPedido: !codart,
    }).catch((err) => {
      console.warn("[printPedidoCompraPdf] matriz no cargada:", err);
      return { rows: [] };
    }),
    codpro
      ? getA3ProveedorFichaPdf({ codpro, idPedc, dbSource }).catch((err) => {
          console.warn("[printPedidoCompraPdf] ficha proveedor:", err);
          return null;
        })
      : Promise.resolve(null),
    codart
      ? getA3ArticuloDescripcion(codart, dbSource).catch((err) => {
          console.warn("[printPedidoCompraPdf] descripción artículo:", err);
          return null;
        })
      : Promise.resolve(null),
  ]);

  let lineasNorm = mapApiLineasToNorm(matrizResult?.rows);
  const proveedorFicha = fichaResult?.ok !== false ? fichaResult : null;
  const articuloDescripcion = normStr(artDescResult?.descripcion);
  const itemDisplay = formatItemWithDescription(codart, articuloDescripcion);

  const matrix = matrixViewFromSavedLineas(lineasNorm);
  const logo = emp.logo ? await loadPdfImage(emp.logo).catch(() => null) : null;

  const colCount = matrix?.tallasH?.length || 0;
  const orientation = colCount > 7 ? "l" : "p";
  const doc = new jsPDF(orientation, "mm", "a4");
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const marginLeft = 14;
  const marginRight = 14;
  const contentW = pageW - marginLeft - marginRight;

  const proveedor = String(
    proveedorFicha?.nombre || row?.ProveedorNombre || row?.CodPro || "—",
  ).trim();
  const referencia = String(row?.Referencia || "").trim();
  const presupuesto = String(
    row?.NombrePresupuesto || row?.Presupuesto || row?.CodPresupuesto || "",
  ).trim();
  const fecha = fmtDate(row?.FechaCompraA3 || row?.FechaA3 || row?.CreatedAt);
  const moneda = String(
    row?.MonedaCompraA3 || row?.MonedaCoste || "EUR",
  ).trim();
  const unidades = Number(row?.UnidadesCompra) || 0;
  const precioUn = resolveUnitPrice(
    row?.UnidadesCompra,
    row?.PrecioCosteUn,
    row?.TotalCompra,
    row?.TotalDocumentoCompra,
  );
  const totalDoc = Number(row?.TotalDocumentoCompra ?? row?.TotalCompra);

  let y = drawPdfHeader(doc, {
    pageW,
    marginLeft,
    marginRight,
    logo,
    emp,
    docTitle: PDF_LABELS.purchaseOrder,
    docRef: poRef,
    docDate: fmtDate(new Date()),
    palette,
  });

  const detailLines = [];
  if (proveedorFicha?.nif) {
    detailLines.push(`${PDF_LABELS.taxId}: ${proveedorFicha.nif}`);
  }
  for (const ln of proveedorFicha?.direccionLines || []) {
    if (normStr(ln)) detailLines.push(ln);
  }
  if (proveedorFicha?.telefono) {
    detailLines.push(`${PDF_LABELS.phone}: ${proveedorFicha.telefono}`);
  }
  if (proveedorFicha?.fax) {
    detailLines.push(`${PDF_LABELS.fax}: ${proveedorFicha.fax}`);
  }
  if (proveedorFicha?.email) {
    detailLines.push(`${PDF_LABELS.email}: ${proveedorFicha.email}`);
  }
  const paymentLabel = formatPaymentTermsForPdf(proveedorFicha);
  if (paymentLabel) {
    detailLines.push(`${PDF_LABELS.paymentTerms}: ${paymentLabel}`);
  }

  y = drawPartyBlock(doc, {
    marginLeft,
    contentW,
    y,
    title: PDF_LABELS.supplier,
    name: proveedor,
    detailLines,
  });

  const infoFields = [
    { label: PDF_LABELS.date, value: fecha },
    { label: PDF_LABELS.item, value: itemDisplay },
    referencia ? { label: PDF_LABELS.reference, value: referencia } : null,
    presupuesto ? { label: PDF_LABELS.budget, value: presupuesto } : null,
    { label: PDF_LABELS.units, value: fmtQtyMatriz(unidades) },
    precioUn != null
      ? { label: PDF_LABELS.unitPrice, value: fmtNum(precioUn) }
      : null,
    Number.isFinite(totalDoc)
      ? {
          label: PDF_LABELS.documentTotal,
          value: fmtNum(totalDoc),
          highlight: true,
        }
      : null,
  ].filter(Boolean);

  y = drawInfoCard(doc, {
    marginLeft,
    contentW,
    y,
    fields: infoFields,
    palette,
    brandRgb,
    totalLabel: PDF_LABELS.documentTotal,
    totalSuffix: moneda,
  });

  drawSectionTitle(doc, {
    x: marginLeft,
    y: y + 0.5,
    title: PDF_LABELS.sizeColorBreakdown,
    rgb: palette.brand,
  });
  y += 6;

  if (matrix && matrix.tallasH.length && matrix.tallasV.length) {
    const activeRows = matrix.tallasV.filter((v) =>
      matrix.tallasH.some((h) => matrix.qtyAt(v.CODTALLA, h.CODTALLA) > 0),
    );

    const head = [
      [
        "Color",
        ...matrix.tallasH.map((h) => sizeColLabel(h, empresaKey)),
        "Total",
      ],
    ];

    const body = activeRows.map((v) => {
      const cells = [colorLabel(v)];
      let rowSum = 0;
      for (const h of matrix.tallasH) {
        const q = matrix.qtyAt(v.CODTALLA, h.CODTALLA);
        rowSum += q;
        cells.push(q > 0 ? fmtQtyMatriz(q) : "");
      }
      cells.push(fmtQtyMatriz(rowSum));
      return cells;
    });

    const foot = [
      [
        PDF_LABELS.columnTotal,
        ...matrix.colTotals.map((ct) => fmtQtyMatriz(ct)),
        fmtQtyMatriz(matrix.total),
      ],
    ];

    const [hr, hg, hb] = palette.brand;

    doc.autoTable({
      startY: y,
      head,
      body: [...body, ...foot],
      margin: { left: marginLeft, right: marginRight },
      theme: "plain",
      styles: {
        fontSize: 7.5,
        cellPadding: { top: 2.2, right: 2, bottom: 2.2, left: 2 },
        lineColor: palette.border,
        lineWidth: 0.15,
        textColor: [38, 44, 52],
        valign: "middle",
      },
      headStyles: {
        fillColor: palette.tableHead,
        textColor: palette.tableHeadText,
        fontStyle: "bold",
        fontSize: 8,
        cellPadding: { top: 2.8, right: 2, bottom: 2.8, left: 2 },
      },
      alternateRowStyles: {
        fillColor: palette.rowAlt,
      },
      columnStyles: {
        0: { fontStyle: "bold", cellWidth: "auto" },
      },
      footStyles: {
        fontStyle: "bold",
        fillColor: palette.rowFoot,
        textColor: [28, 32, 38],
      },
      didParseCell: (data) => {
        if (data.section === "body" && data.row.index === body.length) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fillColor = palette.rowFoot;
        }
        if (
          data.section === "body" &&
          data.column.index > 0 &&
          data.column.index === data.table.columns.length - 1
        ) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.textColor = [hr, hg, hb];
        }
        if (data.section === "head" && data.column.index > 0) {
          data.cell.styles.halign = "center";
        }
        if (data.section === "body" && data.column.index > 0) {
          data.cell.styles.halign = "center";
        }
      },
    });
  } else {
    doc.setFillColor(248, 250, 252);
    doc.setDrawColor(220, 224, 230);
    doc.roundedRect(marginLeft, y, contentW, 18, 2.5, 2.5, "FD");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(100, 108, 118);
    doc.text(
      lineasNorm.length
        ? PDF_LABELS.matrixMixedFamilies
        : PDF_LABELS.noSizeColorBreakdown,
      marginLeft + 5,
      y + 8,
    );
    if (unidades > 0) {
      doc.setFont("helvetica", "bold");
      doc.setTextColor(38, 44, 52);
      doc.text(
        PDF_LABELS.totalOrderedQty(fmtQtyMatriz(unidades)),
        marginLeft + 5,
        y + 14,
      );
    }
  }

  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i);
    drawPageFooter(
      doc,
      pageW,
      marginLeft,
      marginRight,
      pageH,
      i,
      emp,
      palette,
      PDF_LABELS.page,
    );
  }

  const safeName = poRef.replace(/[/\\?%*:|"<>]/g, "-");
  const empresaSlug = String(emp.key || "empresa").replace(/_/g, "-");
  doc.save(`Pedido_Compra_${empresaSlug}_${safeName}.pdf`);
}
